(() => {
  class AvidProductConfigurator {
    constructor(root) {
      this.root = root;
      this.form = document.getElementById(root.dataset.formId || '');
      this.groupsNode = root.querySelector('[data-avid-configurator-groups]');
      this.statusNode = root.querySelector('[data-avid-configurator-status]');
      this.errorNode = root.querySelector('[data-avid-configurator-error]');
      this.versionNode = root.querySelector('[data-avid-configurator-version]');
      this.previewNode = root.querySelector('[data-avid-configurator-preview]');
      this.previewReferenceNode = root.querySelector('[data-avid-configurator-preview-reference]');
      this.totalNode = root.querySelector('[data-avid-configurator-total]');
      this.checkoutLink = root.querySelector('[data-avid-configurator-checkout]');
      this.required = root.dataset.required === 'true';
      this.contract = null;
      this.commerceCapability = null;
      this.previewRequest = null;
      this.submitting = false;
      this.loading = false;
      this.dealerPrices = new Map();
      this.priceRequest = 0;
      this.variantSelect = this.form?.querySelector('[data-variant-select]');
      this.previewButton = this.form?.querySelector('[name="add"]');
      this.quantityInput = this.form?.querySelector('[name="quantity"]');
      if (!this.form || !this.groupsNode) return;
      this.simulation = window.__AVID_OPTION_SIMULATION__ || null;
      if (this.simulation) { this.initializeSimulation(); return; }
      this.form.addEventListener('submit', (event) => this.onSubmit(event), true);
      if (this.previewButton?.type === 'button') {
        this.previewButton.addEventListener('click', (event) => this.onSubmit(event), true);
      }
      this.quantityInput?.addEventListener('change', () => {
        this.previewRequest = null;
        this.clearPreview();
        this.updateTotal();
        this.publishSelectionState();
      });
      this.quantityInput?.addEventListener('input', () => this.updateTotal());
      this.variantSelect?.addEventListener('change', () => this.load(this.variantSelect.value));
      this.load(root.dataset.variantId || this.variantSelect?.value || '');
    }

    initializeSimulation() {
      const send = (message) => window.parent.postMessage({ type: 'avid:option-simulation', nonce: this.simulation.nonce, ...message }, '*');
      this.root.hidden = false;
      document.addEventListener('click', event => { if (event.target.closest('a')) event.preventDefault(); }, true);
      this.contract = { groups: [] };
      this.root.dataset.parentPriceCents = String(this.simulation.parentPriceCents);
      this.form.querySelectorAll('[name="add"]').forEach(button => {
        button.disabled = false;
        button.addEventListener('click', event => { event.preventDefault(); event.stopImmediatePropagation(); this.simulateCart(); }, true);
      });
      this.form.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); this.simulateCart(); }, true);
      this.quantityInput?.addEventListener('input', () => this.updateTotal());
      this.variantSelect?.addEventListener('change', () => send({ variantId: this.variantSelect.value }));
      // The simulation uses the production renderer. Only its input and cart destination differ.
      window.addEventListener('message', event => {
        const message = event.data;
        if (event.source !== window.parent || message?.type !== 'avid:option-simulation' || message.nonce !== this.simulation.nonce) return;
        if (!message.configuration || !Array.isArray(message.configuration.groups)) return;
        const controlKey = node => `${node.closest('[data-binding-id]')?.dataset.bindingId}:${node.dataset.choiceQuantity ? `quantity:${node.dataset.choiceQuantity}` : node.dataset.acknowledgementFor ? `ack:${node.dataset.acknowledgementFor}` : node.dataset.choiceId || node.name || node.tagName}`;
        const current = new Map([...this.groupsNode.querySelectorAll('input,select,textarea')].map(node => [controlKey(node), { value: node.value, checked: node.checked }]));
        this.contract = message.configuration;
        this.form.querySelectorAll('[data-titanium-anodizing], [data-titanium-finish-selector], [data-titanium-finish-options]').forEach(node => { node.hidden = message.configuration.groups.length > 0; });
        this.groupsNode.replaceChildren(); this.renderGroups();
        this.groupsNode.querySelectorAll('input,select,textarea').forEach(node => { const prior = current.get(controlKey(node)); if (prior) { node.value = prior.value; if ('checked' in node) node.checked = prior.checked; } });
        this.statusNode.hidden = true; this.versionNode.textContent = 'Preview of your options';
        this.groupsNode.querySelectorAll('[data-binding-id]').forEach(node => { node.style.outline = node.dataset.bindingId === message.selectedFieldKey ? '2px solid currentColor' : ''; node.style.outlineOffset = '4px'; });
        document.querySelectorAll('.shopify-section-group-header-group,.shopify-section-group-footer-group,.product-detail-region,.product-reviews,.product-related,.product-reference-specs').forEach(node => { node.hidden = message.focus === 'product'; });
        this.updateTotal();
      });
      const measure = () => send({ height: Math.ceil(document.body.getBoundingClientRect().height) });
      new ResizeObserver(measure).observe(document.body);
      send({ ready: true }); measure();
    }

    simulateCart() {
      this.setError('');
      try {
        const selections = this.collectSelections();
        const parentQuantity = Number(this.quantityInput?.value || 1);
        if (!Number.isSafeInteger(parentQuantity) || parentQuantity < 1) throw new Error('Choose a positive whole-number product quantity.');
        let summary = this.root.querySelector('[data-simulated-cart]');
        if (!summary) { summary = document.createElement('div'); summary.dataset.simulatedCart = ''; summary.setAttribute('role', 'status'); this.root.append(summary); }
        summary.replaceChildren();
        const heading = document.createElement('h3'); heading.textContent = 'Simulated cart'; summary.append(heading);
        const parent = document.createElement('p'); parent.textContent = `${document.querySelector('h1')?.textContent?.trim() || 'Product'} × ${parentQuantity}`; summary.append(parent);
        for (const selection of selections) {
          const group = this.contract.groups.find(group => group.bindingId === selection.bindingId);
          const choice = group.choices.find(choice => choice.choiceId === selection.choiceId);
          const row = document.createElement('p'); row.textContent = `${group.heading}: ${selection.inputValue || choice.label}${choice.choiceKind === 'exact_child' ? ` × ${parentQuantity * Number(choice.childQuantity || 1) * selection.quantity}` : ''}`; summary.append(row);
        }
        this.updateTotal();
        const total = document.createElement('strong'); total.textContent = this.totalNode?.textContent || ''; summary.append(total);
        const note = document.createElement('p'); note.textContent = 'Simulation only. Your shopping cart has not changed.'; summary.append(note);
      } catch (error) { this.setError(error.message); }
    }

    async load(variantId) {
      this.priceRequest += 1;
      this.dealerPrices = new Map();
      this.root.dataset.variantId = variantId;
      const selectedVariant = this.variantSelect?.selectedOptions?.[0];
      if (selectedVariant?.dataset.priceCents) this.root.dataset.parentPriceCents = selectedVariant.dataset.priceCents;
      const price = this.form.querySelector('[data-product-selection-pricing] [data-dealer-pricing]');
      if (price) delete price.dataset.configurationPricing;
      this.loading = true;
      this.contract = null;
      this.commerceCapability = null;
      this.previewRequest = null;
      this.clearPreview();
      this.groupsNode.replaceChildren();
      this.setError('');
      if (!variantId) {
        this.loading = false;
        return this.unavailable('A valid product configuration is not selected.');
      }
      this.root.hidden = false;
      this.statusNode.hidden = false;
      this.statusNode.textContent = 'Loading available product options…';
      this.setPurchaseBlocked(this.required);
      try {
        const response = await fetch(`${this.root.dataset.proxyUrl}?variant_id=${encodeURIComponent(variantId)}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (response.status === 404 && !this.required) {
          this.loading = false;
          this.root.hidden = true;
          this.setPurchaseBlocked(false);
          return;
        }
        const payload = await response.json().catch(() => ({}));
        if (payload.configurationRequired === true) this.required = true;
        if (!response.ok || !payload.configuration) throw new Error(payload.error || 'Product options are temporarily unavailable.');
        if (!this.isSupportedCapability(payload.commerceCapability)) {
          throw new Error('Configured purchasing is not safely available.');
        }
        this.contract = payload.configuration;
        this.commerceCapability = payload.commerceCapability;
        this.setPreviewButtonLabel();
        this.versionNode.textContent = 'Current product options';
        this.statusNode.hidden = true;
        this.renderGroups();
        this.updateTotal();
        void this.loadDealerPrices(variantId);
        this.publishSelectionState();
        this.loading = false;
        this.setPurchaseBlocked(false);
      } catch (error) {
        this.loading = false;
        this.unavailable(error instanceof Error ? error.message : 'Product options are temporarily unavailable.');
      }
    }

    renderGroups() {
      const groups = Array.isArray(this.contract?.groups) ? this.contract.groups : [];
      for (const group of groups) this.groupsNode.append(this.renderGroup(group));
      this.updateVisibility();
    }

    renderGroup(group) {
      if (['heading', 'paragraph', 'divider'].includes(group.presentation)) {
        const block = document.createElement(group.presentation === 'divider' ? 'hr' : group.presentation === 'heading' ? 'h3' : 'p');
        block.dataset.bindingId = group.bindingId;
        if (group.presentation !== 'divider') block.textContent = group.presentation === 'paragraph' ? group.description || group.heading : group.heading;
        return block;
      }
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'avid-configurator__group';
      fieldset.dataset.bindingId = group.bindingId;
      const legend = document.createElement('legend');
      const heading = document.createElement('div');
      heading.className = 'avid-configurator__group-heading';
      const title = document.createElement('strong');
      title.textContent = group.heading;
      const requirement = document.createElement('span');
      requirement.textContent = group.requirementKind === 'optional' ? 'Optional' : 'Required';
      heading.append(title, requirement);
      legend.append(heading);
      if (group.description) {
        const description = document.createElement('p');
        description.className = 'avid-configurator__group-description';
        description.textContent = group.description;
        legend.append(description);
      }
      const choices = document.createElement('div');
      choices.className = 'avid-configurator__choices';
      const multiple = Number(group.maximumSelections || 1) > 1;
      const availableChoices = Array.isArray(group.choices) ? group.choices : [];
      const searchable = (group.presentation === 'dropdown' || (!group.presentation && availableChoices.length > 6)) && !multiple && !availableChoices.some((choice) => this.isAcknowledgement(choice));
      if (['text', 'multiline', 'number'].includes(group.presentation)) {
        const input = document.createElement(group.presentation === 'multiline' ? 'textarea' : 'input');
        if (group.presentation !== 'multiline') input.type = group.presentation === 'number' ? 'number' : 'text';
        if (group.presentation === 'number') { input.min = group.numberRules?.minimum; input.max = group.numberRules?.maximum; input.step = group.numberRules?.step; }
        input.dataset.optionText = '';
        input.maxLength = Math.min(500, Math.max(1, Number(group.textMaxLength || 100)));
        input.required = Number(group.minimumSelections || 0) > 0;
        input.setAttribute('aria-label', group.heading);
        input.placeholder = group.presentation === 'number' ? `${group.numberRules?.minimum} to ${group.numberRules?.maximum}` : `Up to ${input.maxLength} characters`;
        input.addEventListener('input', () => { this.previewRequest = null; this.clearPreview(); this.updateTotal(); this.publishSelectionState(); });
        choices.append(input);
      } else if (searchable) {
        choices.append(this.renderSearchableChoices(group, availableChoices));
      } else {
        for (const choice of availableChoices) choices.append(this.renderChoice(group, choice, multiple));
      }
      fieldset.append(legend, choices);
      return fieldset;
    }

    renderSearchableChoices(group, choices) {
      const wrapper = document.createElement('label');
      wrapper.className = 'avid-configurator__searchable';
      const search = document.createElement('input');
      search.type = 'search';
      search.placeholder = `Search ${String(group.heading || 'options').toLowerCase()}`;
      search.setAttribute('aria-label', `Search ${group.heading}`);
      const select = document.createElement('select');
      select.dataset.choiceSelect = '';
      select.name = `avid-config-${group.bindingId}`;
      select.required = Number(group.minimumSelections || 0) > 0;
      let rendered = false;
      const render = (needle = '') => {
        const current = rendered ? select.value : null;
        select.replaceChildren(new Option(select.required ? 'Choose an option' : 'No additional option', ''));
        choices.filter((choice) => choice.choiceId === current || !needle || `${choice.label} ${choice.description || ''} ${choice.child?.sku || ''}`.toLowerCase().includes(needle))
          .forEach((choice) => {
            const option = new Option(this.choiceOptionLabel(choice), choice.choiceId, choice.default === true, choice.default === true);
            option.disabled = this.choiceUnavailable(choice);
            select.add(option);
          });
        if (current !== null && [...select.options].some((option) => option.value === current)) select.value = current;
        rendered = true;
      };
      render();
      search.addEventListener('input', () => render(search.value.trim().toLowerCase()));
      const quantity = document.createElement('span');
      const updateQuantity = () => { quantity.replaceChildren(); const choice = choices.find(choice => choice.choiceId === select.value); if (choice?.quantityRules) quantity.append(this.renderQuantity(choice)); };
      select.addEventListener('change', () => { updateQuantity(); this.previewRequest = null; this.clearPreview(); this.updateTotal(); this.publishSelectionState(); });
      if (choices.length > 6) wrapper.append(search);
      wrapper.append(select, quantity); updateQuantity();
      return wrapper;
    }

    choiceOptionLabel(choice) {
      const cents = Number(choice.childPriceRule?.resolvedUnitPriceCents);
      const suffix = choice.choiceKind === 'exact_child' && Number.isSafeInteger(cents) && cents > 0
        ? ` · +${this.formatMoney(cents * Number(choice.childQuantity || 1) / 100, choice.childPriceRule?.currencyCode || 'USD')}` : '';
      return `${choice.label}${choice.child?.sku ? ` · ${choice.child.sku}` : ''}${suffix}`;
    }

    renderChoice(group, choice, multiple) {
      const label = document.createElement('label');
      label.className = 'avid-configurator__choice';
      if (group.presentation === 'button') label.classList.add('avid-configurator__choice--button');
      const input = document.createElement('input');
      input.type = multiple || group.presentation === 'checkbox' || group.presentation === 'acknowledgement' ? 'checkbox' : 'radio';
      input.name = `avid-config-${group.bindingId}`;
      input.value = choice.choiceId;
      input.dataset.choiceId = choice.choiceId;
      input.dataset.choiceKind = choice.choiceKind;
      input.checked = choice.default === true && !this.isAcknowledgement(choice);
      input.disabled = this.choiceUnavailable(choice);
      input.addEventListener('change', () => this.handleChoiceChange(label, input));
      const governedFinishImage = this.form?.querySelector(`[data-titanium-finish-option][data-finish-label="${CSS.escape(String(choice.label || ''))}"] img`)?.src;
      const colorSwatch = group.presentation === 'color_swatch' && /^#[\da-f]{6}$/i.test(choice.media?.color || '');
      const media = document.createElement(!colorSwatch && (choice.media?.imageUrl || governedFinishImage || choice.child?.imageUrl) ? 'button' : 'span');
      if (media.tagName === 'BUTTON') media.type = 'button';
      media.className = 'avid-configurator__choice-media';
      if (group.presentation === 'color_swatch' && /^#[\da-f]{6}$/i.test(choice.media?.color || '')) {
        media.style.backgroundColor = choice.media.color;
        media.setAttribute('aria-hidden', 'true');
      } else if (choice.media?.imageUrl || governedFinishImage || choice.child?.imageUrl) {
        const image = document.createElement('img');
        image.src = choice.media?.imageUrl || governedFinishImage || choice.child.imageUrl;
        image.alt = '';
        image.loading = 'lazy';
        media.setAttribute('aria-label', `View larger image of ${choice.child?.title || choice.label}`);
        media.setAttribute('aria-haspopup', 'dialog');
        media.addEventListener('click', event => {
          event.preventDefault();
          event.stopPropagation();
          this.openChoiceImage(image.src, choice.child?.title || choice.label, media);
        });
        image.addEventListener('error', () => { media.hidden = true; });
        media.append(image);
      } else {
        media.hidden = true;
      }
      const copy = document.createElement('span');
      copy.className = 'avid-configurator__choice-copy';
      const choiceTitle = document.createElement('strong');
      choiceTitle.textContent = choice.label;
      copy.append(choiceTitle);
      if (choice.description && !/^Reviewed Aris source label:/i.test(choice.description.trim())) {
        const description = document.createElement('small');
        description.textContent = choice.description;
        copy.append(description);
      }
      const meta = document.createElement('span');
      meta.className = 'avid-configurator__choice-meta';
      if (choice.child?.sku && !/^AVID-SVC-/i.test(choice.child.sku)) meta.append(this.meta(choice.child.sku));
      const resolvedCents = Number(choice.childPriceRule?.resolvedUnitPriceCents);
      const currency = String(choice.childPriceRule?.currencyCode || 'USD');
      if (/^raw\s*ti(?:tanium)?$/i.test(String(choice.label || '').trim())) {
        meta.append(this.meta('Standard finish'));
      } else if (choice.choiceKind === 'exact_child' && Number.isSafeInteger(resolvedCents) && resolvedCents > 0) {
        meta.append(this.meta(`+${this.formatMoney(resolvedCents * Number(choice.childQuantity || 1) / 100, currency)}`));
      } else if (choice.childPriceTreatment && choice.childPriceTreatment !== 'not_applicable') {
        meta.append(this.meta(this.priceTreatment(choice.childPriceTreatment)));
      }
      if (choice.child?.available === false && choice.child?.backorderable === true) {
        meta.append(this.meta(choice.child.leadTimeText || 'Backorderable · AVID will confirm the ship date after ordering'));
      } else if (this.choiceUnavailable(choice)) {
        meta.append(this.meta('Unavailable'));
      }
      copy.append(meta);
      label.append(input, media, copy);
      if (this.isAcknowledgement(choice)) label.append(this.renderAcknowledgement(choice, input));
      if (choice.quantityRules) label.append(this.renderQuantity(choice));
      return label;
    }

    openChoiceImage(src, title, trigger) {
      if (!this.imageDialog) {
        const dialog = document.createElement('dialog');
        dialog.className = 'avid-configurator__image-dialog';
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'button button--secondary';
        close.textContent = 'Close image';
        close.addEventListener('click', () => dialog.close());
        const image = document.createElement('img');
        const caption = document.createElement('p');
        dialog.append(close, image, caption);
        dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
        dialog.addEventListener('close', () => {
          document.body.style.overflow = this.imagePreviousOverflow;
          this.imageTrigger?.focus();
        });
        document.body.append(dialog);
        this.imageDialog = dialog;
      }
      this.imageTrigger = trigger;
      this.imageDialog.setAttribute('aria-label', `${title} image`);
      const image = this.imageDialog.querySelector('img');
      image.src = src;
      image.alt = title;
      this.imageDialog.querySelector('p').textContent = title;
      this.imagePreviousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.imageDialog.showModal();
    }

    renderQuantity(choice) {
      const input = document.createElement('input'); input.type = 'number'; input.dataset.choiceQuantity = choice.choiceId;
      input.min = choice.quantityRules.minimum; input.max = choice.quantityRules.maximum; input.step = '1'; input.value = choice.quantityRules.default;
      input.setAttribute('aria-label', `Quantity for ${choice.label} per main product`);
      input.addEventListener('click', event => event.stopPropagation());
      input.addEventListener('input', () => { this.previewRequest = null; this.clearPreview(); this.updateTotal(); this.publishSelectionState(); });
      return input;
    }

    groupVisible(group, visited = new Set()) {
      if (!group.visibility) return true;
      if (visited.has(group.fieldKey || group.bindingId)) return false;
      visited.add(group.fieldKey || group.bindingId);
      const source = this.contract.groups.find(candidate => candidate.fieldKey === group.visibility.fieldKey);
      if (!source || !this.groupVisible(source, visited)) return false;
      const fieldset = this.groupsNode.querySelector(`[data-binding-id="${CSS.escape(source.bindingId)}"]`);
      const selected = fieldset?.querySelector('[data-choice-select]');
      const ids = selected ? [selected.value] : [...(fieldset?.querySelectorAll('[data-choice-id]:checked') || [])].map(input => input.value);
      return source.choices.some(choice => ids.includes(choice.choiceId) && group.visibility.choiceCodes.includes(choice.code));
    }

    updateVisibility() {
      for (const group of this.contract?.groups || []) {
        const node = this.groupsNode.querySelector(`[data-binding-id="${CSS.escape(group.bindingId)}"]`); if (!node) continue;
        node.hidden = !this.groupVisible(group);
        node.querySelectorAll('input,select,textarea').forEach(input => {
          const choice = group.choices?.find(choice => choice.choiceId === input.dataset.choiceId);
          const owner = input.dataset.acknowledgementFor ? node.querySelector(`[data-choice-id="${CSS.escape(input.dataset.acknowledgementFor)}"]`) : null;
          input.disabled = node.hidden || (choice ? this.choiceUnavailable(choice) : false) || (input.dataset.acknowledgementFor ? !owner?.checked : false);
          if (node.hidden) { if (input.type === 'checkbox' || input.type === 'radio') input.checked = false; else if (!input.dataset.choiceQuantity) input.value = ''; }
        });
      }
    }

    renderAcknowledgement(choice, choiceInput) {
      const box = document.createElement('span');
      box.className = 'avid-configurator__ack';
      const statement = document.createElement('p');
      statement.textContent = choice.acknowledgementStatement;
      const confirmationLabel = document.createElement('label');
      const confirmation = document.createElement('input');
      confirmation.type = 'checkbox';
      confirmation.dataset.acknowledgementFor = choice.choiceId;
      confirmation.disabled = !choiceInput.checked;
      confirmationLabel.append(confirmation, document.createTextNode(' I have read and accept this requirement.'));
      box.addEventListener('click', (event) => event.stopPropagation());
      box.append(statement, confirmationLabel);
      return box;
    }

    handleChoiceChange(label, input) {
      const fieldset = label.closest('fieldset');
      this.previewRequest = null;
      this.clearPreview();
      fieldset?.querySelectorAll('[data-acknowledgement-for]').forEach((ack) => {
        const owner = fieldset.querySelector(`[data-choice-id="${CSS.escape(ack.dataset.acknowledgementFor)}"]`);
        ack.disabled = !owner?.checked;
        if (ack.disabled) ack.checked = false;
      });
      this.setError('');
      this.updateTotal();
      this.publishSelectionState();
    }

    async onSubmit(event) {
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      if (this.loading) {
        event.preventDefault();
        this.setError('Wait for the product options to finish loading.');
        return;
      }
      if (!this.contract) {
        if (this.required) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setError('Complete the available product options before saving this configuration.');
        }
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      try {
        const selections = this.collectSelections();
        const quantity = Number(this.form.querySelector('[name="quantity"]')?.value || 1);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) throw new Error('Choose a whole-number quantity between 1 and 100.');
        this.setBusy(true, 'Confirming configuration…');
        const previewRequest = this.previewRequestFor({
          shopifyVariantId: this.variantSelect?.value || this.root.dataset.variantId,
          productConfigurationVersionId: this.contract.productConfigurationVersionId,
          configurationFingerprint: this.contract.configurationFingerprint,
          quantity,
          selections
        });
        const response = await fetch(`${this.root.dataset.proxyUrl}/instances`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(previewRequest.body)
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload.receipt || !payload.instance || !payload.preview) {
          throw new Error(payload.error || 'The configuration preview could not be saved.');
        }
        if (!this.isSupportedCapability(payload.commerceCapability)) {
          throw new Error('The AVID configuration service did not return a supported commerce contract.');
        }
        if (this.previewRequest !== previewRequest) throw new Error('Your options changed while confirmation was processing. Review them and confirm again.');
        if (payload.commerceCapability.mode === 'native_nested_cart') {
          await this.addConfiguredSystemToCart(payload);
          // A receipt signs one cart addition and its quantities. Reusing it after
          // success merges the signed lines and invalidates their quantity proof.
          if (this.previewRequest === previewRequest) this.previewRequest = null;
        }
        else this.showSavedPreview(payload);
        this.setBusy(false);
      } catch (error) {
        this.setBusy(false);
        this.setError(error instanceof Error ? error.message : 'The configuration preview could not be saved.');
      }
    }

    collectSelections() {
      const selected = [];
      for (const group of this.contract.groups || []) {
        if (['heading', 'paragraph', 'divider'].includes(group.presentation) || !this.groupVisible(group)) continue;
        const fieldset = this.groupsNode.querySelector(`[data-binding-id="${CSS.escape(group.bindingId)}"]`);
        if (['text', 'multiline', 'number'].includes(group.presentation)) {
          const value = String(fieldset?.querySelector('[data-option-text]')?.value || '').trim();
          const limit = Math.min(500, Math.max(1, Number(group.textMaxLength || 100)));
          if ((!value && Number(group.minimumSelections || 0) > 0) || value.length > limit || (group.presentation === 'multiline' ? /[\u0000-\u0009\u000b-\u001f\u007f]/ : /[\u0000-\u001f\u007f]/).test(value)) throw new Error(`Enter up to ${limit} characters for ${group.heading}.`);
          if (value && group.presentation === 'number') { const number = Number(value), rule = group.numberRules; if (!rule || !/^-?[0-9]+(\.[0-9]+)?$/.test(value) || !Number.isFinite(number) || number < rule.minimum || number > rule.maximum || Math.abs((number - rule.minimum) / rule.step - Math.round((number - rule.minimum) / rule.step)) > 0.000001) throw new Error(`Enter a valid number from ${rule?.minimum} to ${rule?.maximum} for ${group.heading}.`); }
          if (value) {
            const choice = group.choices?.[0];
            if (!choice || choice.choiceKind !== 'selection_only') throw new Error('This text option is unavailable.');
            selected.push({ bindingId: group.bindingId, choiceId: choice.choiceId, quantity: 1, inputValue: value });
          }
          continue;
        }
        const select = fieldset?.querySelector('[data-choice-select]');
        const inputs = select?.value ? [{ value: select.value }] : [...(fieldset?.querySelectorAll('[data-choice-id]:checked') || [])];
        const minimum = Number(group.minimumSelections || 0);
        const maximum = Number(group.maximumSelections || 1);
        if (inputs.length < minimum || inputs.length > maximum) throw new Error(`Choose ${minimum === maximum ? minimum : `${minimum}–${maximum}`} option${maximum === 1 ? '' : 's'} for ${group.heading}.`);
        for (const input of inputs) {
          const choice = (group.choices || []).find((candidate) => candidate.choiceId === input.value);
          if (!choice) throw new Error('A selected option is no longer available for this configuration.');
          const selection = { bindingId: group.bindingId, choiceId: choice.choiceId, quantity: 1 };
          if (choice.quantityRules) { const rule = choice.quantityRules; const quantity = Number(fieldset.querySelector(`[data-choice-quantity="${CSS.escape(choice.choiceId)}"]`)?.value || rule.default); if (!Number.isSafeInteger(quantity) || quantity < rule.minimum || quantity > rule.maximum) throw new Error(`Choose between ${rule.minimum} and ${rule.maximum} items for ${choice.label}.`); selection.quantity = quantity; }
          if (this.isAcknowledgement(choice)) {
            const ack = fieldset.querySelector(`[data-acknowledgement-for="${CSS.escape(choice.choiceId)}"]`);
            if (!ack?.checked) throw new Error(`Acknowledge the requirement for ${choice.label}.`);
            const acknowledgedAt = new Date().toISOString();
            selection.acknowledgementStatement = choice.acknowledgementStatement;
            selection.acknowledgementStatementVersion = choice.acknowledgementStatementVersion;
            selection.acknowledgedAt = acknowledgedAt;
            selection.acknowledgementEvidence = this.acknowledgementEvidence(choice, acknowledgedAt);
          }
          selected.push(selection);
        }
      }
      return selected;
    }

    updateTotal() {
      if (!this.totalNode || !this.contract) return;
      this.updateVisibility();
      const quantity = Math.max(1, Number(this.quantityInput?.value || 1));
      let cents = Number(this.root.dataset.parentPriceCents || 0);
      const parentId = String(this.contract.shopifyParentVariantId || this.root.dataset.variantId).split('/').pop();
      const parentPrice = this.dealerPrices.get(parentId);
      const currency = this.root.dataset.currency || 'USD';
      let dealerCents = parentPrice && parentPrice.currencyCode === currency ? Math.min(cents, parentPrice.dealerPriceCents) : null;
      try {
        const selected = this.collectSelections();
        for (const selection of selected) {
          const group = (this.contract.groups || []).find((candidate) => candidate.bindingId === selection.bindingId);
          const choice = (group?.choices || []).find((candidate) => candidate.choiceId === selection.choiceId);
          const treatment = choice?.childPriceTreatment;
          const addition = Number(choice?.childPriceRule?.resolvedUnitPriceCents || 0);
          if (choice?.choiceKind === 'exact_child' && treatment !== 'included' && Number.isSafeInteger(addition) && addition > 0) {
            const units = Number(choice.childQuantity || 1) * selection.quantity;
            cents += addition * units;
            // Nested children use the signed configuration price in Shopify.
            // Dealer discounts apply to the parent and never stack on those children.
            if (dealerCents !== null) dealerCents += addition * units;
          }
        }
      } catch (_error) { /* Incomplete required choices still show the parent starting price. */ }
      this.totalNode.textContent = this.formatMoney((cents * quantity) / 100, this.root.dataset.currency || 'USD');
      this.renderSelectionPricing(cents, dealerCents, quantity, currency);
    }

    async loadDealerPrices(variantId) {
      const request = ++this.priceRequest;
      this.dealerPrices = new Map();
      const ids = [variantId, ...(this.contract?.groups || []).flatMap(group => (group.choices || []).map(choice => choice.child?.shopifyVariantId))]
        .map(id => String(id || '').split('/').pop()).filter(id => /^[1-9][0-9]*$/.test(id));
      try {
        const prices = await window.AvidDealerPricing?.pricesFor(ids);
        if (request !== this.priceRequest) return;
        this.dealerPrices = prices || new Map();
      } catch (_error) { /* Public prices remain the fallback without verified dealer prices. */ }
      if (request === this.priceRequest) this.updateTotal();
    }

    renderSelectionPricing(retail, dealer, quantity, currency) {
      const summary = this.form.querySelector('[data-product-selection-pricing]');
      if (!summary) return; // Embedded option previews retain their own total.
      const price = summary.querySelector('[data-dealer-pricing]');
      if (!price) return;
      price.dataset.configurationPricing = 'true';
      const money = cents => this.formatMoney(cents / 100, currency);
      const isDealer = dealer !== null && dealer < retail;
      price.querySelector('.product-price__current').textContent = money(retail);
      price.querySelector('.product-price__current').classList.remove('product-price__current--sale');
      price.classList.remove('price--sale');
      price.querySelectorAll('[data-public-compare-at], [data-sale-badge]').forEach(node => { node.hidden = true; });
      const mapLabel = price.querySelector('[data-dealer-map-label]');
      if (mapLabel) mapLabel.hidden = !isDealer;
      const dealerLine = price.querySelector('[data-dealer-price-line]');
      if (dealerLine) {
        dealerLine.hidden = !isDealer;
        dealerLine.querySelector('[data-dealer-price-value]').textContent = isDealer ? money(dealer) : '';
      }
      price.classList.toggle('price--dealer', isDealer);
      summary.querySelector('[data-product-selection-price-note]').hidden = false;
      this.totalNode.closest('.avid-configurator__total').hidden = true;
      const total = this.form.querySelector('[data-product-running-total]');
      if (total) {
        total.querySelector('[data-product-running-total-label]').textContent = isDealer ? 'Dealer total' : 'Total';
        total.querySelector('[data-product-running-total-value]').textContent = money((isDealer ? dealer : retail) * quantity);
        total.querySelector('[data-product-running-total-note]').textContent = `${quantity} × selected configuration`;
      }
      const mobile = this.form.closest('[data-product-root]')?.querySelector('[data-mobile-purchase-price]');
      if (mobile) mobile.textContent = money((isDealer ? dealer : retail) * quantity);
    }

    publishSelectionState() {
      window.AvidProductConfigurationState ||= {};
      let selections = [];
      let valid = !this.contract;
      try {
        selections = this.contract ? this.collectSelections() : [];
        valid = true;
      } catch (_error) { /* Keep incomplete state visible so Planned Builds can explain why saving is blocked. */ }
      window.AvidProductConfigurationState[this.root.dataset.formId] = this.contract ? {
        productConfigurationVersionId: this.contract.productConfigurationVersionId,
        configurationFingerprint: this.contract.configurationFingerprint,
        selections,
        valid
      } : null;
    }

    async addConfiguredSystemToCart(payload) {
      const proof = payload.proofV2;
      if (!proof || !Array.isArray(proof.children)) throw new Error('The signed configured-cart proof is incomplete.');
      const parentId = Number(String(proof.parentVariantGid || '').replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
      if (!Number.isSafeInteger(parentId)) throw new Error('The configured parent identity is invalid.');
      const commonParent = {
        _avid_configuration_instance_id: payload.instance.id,
        _avid_configuration_receipt: payload.receipt,
        _avid_configuration_fingerprint: proof.configurationFingerprint,
        _avid_configuration_version: String(proof.configurationVersion),
        _avid_configuration_choices: proof.choicesJson,
        _avid_configuration_line_role: 'parent',
        _avid_configuration_parent_signature_v2: proof.parentSignature,
        'AVID configuration': 'Configured system'
      };
      this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (input.disabled || (['checkbox', 'radio'].includes(input.type) && !input.checked)) return;
        if (match && !match[1].startsWith('_avid_configuration_') && input.value) commonParent[match[1]] = input.value;
      });
      const selections = JSON.parse(proof.choicesJson);
      (this.contract.groups || []).forEach((group, index) => {
        const values = selections.filter(selection => selection.bindingId === group.bindingId).map(selection => selection.inputValue ?? group.choices?.find(choice => choice.choiceId === selection.choiceId)?.label).filter(Boolean);
        if (values.length) commonParent[`${index + 1}. ${group.heading}`] = values.join(', ');
      });
      const items = [{ id: parentId, quantity: proof.parentQuantity, properties: commonParent }];
      proof.children.forEach((child) => {
        const childId = Number(String(child.childVariantGid || '').replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
        if (!Number.isSafeInteger(childId)) throw new Error('A configured child identity is invalid.');
        items.push({
          id: childId,
          quantity: child.quantity,
          parent_id: parentId,
          properties: {
            _avid_configuration_instance_id: payload.instance.id,
            _avid_configuration_receipt: payload.receipt,
            _avid_configuration_instance_line_id: child.instanceLineId,
            _avid_configuration_parent_variant_id: proof.parentVariantGid,
            _avid_configuration_choice_id: child.choiceId,
            _avid_configuration_line_role: 'selected_child',
            _avid_configuration_price_treatment: child.priceTreatment,
            _avid_configuration_unit_price_cents: String(child.resolvedUnitPriceCents),
            _avid_configuration_currency: child.currency,
            _avid_configuration_line_signature_v2: child.lineSignature
          }
        });
      });
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || 'The configured system could not be added to the cart.');
      document.dispatchEvent(new CustomEvent('avid:cart-added', {
        detail: {
          cart: result,
          trigger: this.previewButton,
          message: 'Configured system added to cart.'
        }
      }));
    }

    acknowledgementEvidence(choice, acknowledgedAt) {
      const requirements = choice.acknowledgementEvidenceRequirements || { confirmed: true };
      const evidence = {};
      Object.keys(requirements).forEach((key) => {
        if (/time|date/i.test(key)) evidence[key] = acknowledgedAt;
        else if (/source|channel/i.test(key)) evidence[key] = 'shopify_storefront';
        else if (/statement/i.test(key)) evidence[key] = choice.acknowledgementStatement;
        else evidence[key] = true;
      });
      evidence.interaction = 'explicit_checkbox';
      return evidence;
    }

    showSavedPreview(payload) {
      const instanceId = String(payload.instance?.id || '');
      const receipt = String(payload.receipt || '');
      const preview = payload.preview || {};
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(instanceId)
        || preview.status !== 'saved'
        || preview.mode !== 'preview_only'
        || preview.reference !== instanceId
        || preview.receipt !== receipt
        || !new RegExp(`^${instanceId}\\.[0-9a-f]{64}\\.[0-9a-f]{64}$`, 'i').test(receipt)) {
        throw new Error('The saved AVID configuration preview receipt is invalid.');
      }
      this.root.dataset.previewSaved = 'true';
      if (this.previewReferenceNode) this.previewReferenceNode.textContent = instanceId;
      if (this.checkoutLink) {
        const portalBase = String(this.root.dataset.portalBase || 'https://portal.avidrace.com').replace(/\/$/, '');
        const checkoutQuery = new URLSearchParams({ instance: instanceId, receipt });
        this.checkoutLink.href = `${portalBase}/portal/customer/configured-checkout?${checkoutQuery.toString()}`;
        this.checkoutLink.hidden = false;
      }
      if (this.previewNode) this.previewNode.hidden = false;
      this.setError('');
    }

    clearPreview() {
      delete this.root.dataset.previewSaved;
      if (this.previewReferenceNode) this.previewReferenceNode.textContent = '';
      if (this.checkoutLink) {
        this.checkoutLink.removeAttribute('href');
        this.checkoutLink.hidden = true;
      }
      if (this.previewNode) this.previewNode.hidden = true;
    }

    isPreviewOnlyCapability(capability) {
      return capability?.contractVersion === 1
        && capability.mode === 'preview_only'
        && capability.serverAuthoritative === true
        && capability.ordinaryCartAllowed === false
        && capability.checkoutAllowed === false
        && capability.draftOrderCheckoutActive === false
        && capability.receiptPersistence === 'portal_configuration_instance';
    }

    isSupportedCapability(capability) {
      if (this.isPreviewOnlyCapability(capability)) return true;
      return capability?.contractVersion === 1
        && capability.mode === 'native_nested_cart'
        && capability.serverAuthoritative === true
        && capability.ordinaryCartAllowed === true
        && capability.checkoutAllowed === true
        && capability.draftOrderCheckoutActive === false
        && capability.receiptPersistence === 'portal_configuration_instance';
    }

    setPreviewButtonLabel() {
      const button = this.form.querySelector('[name="add"]');
      if (!button) return;
      const previewLabel = this.commerceCapability?.mode === 'native_nested_cart'
        ? 'Add configured system to cart'
        : button.dataset.previewLabel || 'Save configuration';
      button.dataset.availableLabel = previewLabel;
      const label = button.querySelector('span');
      if (label && button.dataset.avidInventoryDisabled !== 'true' && button.dataset.avidPricingDisabled !== 'true') {
        label.textContent = previewLabel;
      }
    }

    previewRequestFor(input) {
      const identity = JSON.stringify({
        shopifyVariantId: String(input.shopifyVariantId || ''),
        productConfigurationVersionId: input.productConfigurationVersionId,
        configurationFingerprint: input.configurationFingerprint,
        quantity: input.quantity,
        selections: input.selections
          .map((selection) => ({
            bindingId: selection.bindingId,
            choiceId: selection.choiceId,
            quantity: selection.quantity,
            ...(selection.inputValue === undefined ? {} : { inputValue: selection.inputValue })
          }))
          .sort((left, right) => left.bindingId.localeCompare(right.bindingId) || left.choiceId.localeCompare(right.choiceId))
      });
      if (this.previewRequest?.identity === identity) return this.previewRequest;
      this.previewRequest = {
        identity,
        body: {
          ...input,
          idempotencyKey: `storefront-preview-${crypto.randomUUID()}`
        }
      };
      return this.previewRequest;
    }

    unavailable(message) {
      this.setError(message);
      this.statusNode.hidden = true;
      if (this.required) {
        this.root.hidden = false;
        this.setPurchaseBlocked(true);
      } else {
        this.root.hidden = true;
        this.setPurchaseBlocked(false);
      }
    }

    setPurchaseBlocked(blocked) {
      const button = this.form.querySelector('[name="add"]');
      if (!button) return;
      if (blocked) {
        button.disabled = true;
        button.dataset.avidConfigurationDisabled = 'true';
      } else if (button.dataset.avidConfigurationDisabled === 'true') {
        delete button.dataset.avidConfigurationDisabled;
        if (button.dataset.avidInventoryDisabled !== 'true' && button.dataset.avidPricingDisabled !== 'true') button.disabled = false;
      }
    }

    setBusy(busy, message = '') {
      this.submitting = busy;
      this.setPurchaseBlocked(busy);
      if (busy) {
        this.statusNode.hidden = false;
        this.statusNode.textContent = message;
      } else if (this.contract) {
        this.statusNode.hidden = true;
      }
    }

    setError(message) {
      this.errorNode.hidden = !message;
      this.errorNode.textContent = message;
    }

    isAcknowledgement(choice) {
      return choice.choiceKind === 'customer_supplied_acknowledgement' || choice.choiceKind === 'customer_fabricated_acknowledgement';
    }

    choiceUnavailable(choice) {
      if (choice?.choiceKind !== 'exact_child') return false;
      const cents = Number(choice.childPriceRule?.resolvedUnitPriceCents);
      const priceInvalid = !Number.isSafeInteger(cents) || cents < 0
        || (choice.childPriceTreatment === 'included' && cents !== 0);
      return priceInvalid || choice.child?.sellable === false
        || (choice.child?.available === false && choice.child?.backorderable !== true);
    }

    priceTreatment(value) {
      if (value === 'included') return 'Included with kit';
      if (value === 'discounted') return 'Configured discount';
      if (value === 'independent') return 'Added separately';
      return value;
    }

    formatMoney(amount, currency = 'USD') {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(Number(amount));
    }

    meta(text) {
      const span = document.createElement('span');
      span.textContent = text;
      return span;
    }

  }

  const initialize = (scope = document) => scope.querySelectorAll('[data-avid-configurator]').forEach((root) => {
    if (root.dataset.avidConfiguratorReady === 'true') return;
    root.dataset.avidConfiguratorReady = 'true';
    new AvidProductConfigurator(root);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
